import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getProject } from '../services/api';
import { HiOutlineChartBar } from 'react-icons/hi';
import { FiCheckCircle, FiXCircle, FiTrendingUp, FiTarget, FiActivity } from 'react-icons/fi';

export default function Reports() {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getProject(id);
      setProject(data);
    } catch { /* handled by 401 interceptor */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!project) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement...</span>
    </div>
  );

  const allTC = project.requirements.flatMap(r => r.test_cases);
  const total = allTC.length;
  const stats = { NT: 0, EC: 0, OK: 0, KO: 0, BL: 0 };
  allTC.forEach(tc => { stats[tc.statut] = (stats[tc.statut] || 0) + 1; });

  const executed = stats.OK + stats.KO;
  const executedPct = total > 0 ? ((executed / total) * 100).toFixed(1) : 0;
  const successPct = executed > 0 ? ((stats.OK / executed) * 100).toFixed(1) : 0;
  const failPct = executed > 0 ? ((stats.KO / executed) * 100).toFixed(1) : 0;

  const totalReqs = project.requirements.length;
  const coveredReqs = project.requirements.filter(r => r.test_cases.length > 0).length;
  const coveragePct = totalReqs > 0 ? ((coveredReqs / totalReqs) * 100).toFixed(0) : 0;

  const reqStats = project.requirements.map(r => {
    const tc = r.test_cases;
    const ok = tc.filter(t => t.statut === 'OK').length;
    const ko = tc.filter(t => t.statut === 'KO').length;
    return { ...r, total: tc.length, ok, ko, pct: tc.length > 0 ? ((ok / tc.length) * 100).toFixed(0) : 0 };
  });

  const donutData = [
    { key: 'OK', value: stats.OK, color: 'var(--green-500)', label: 'Réussis' },
    { key: 'KO', value: stats.KO, color: 'var(--danger)', label: 'Échoués' },
    { key: 'EC', value: stats.EC, color: 'var(--info)', label: 'En cours' },
    { key: 'BL', value: stats.BL, color: 'var(--warning)', label: 'Bloqués' },
    { key: 'NT', value: stats.NT, color: 'var(--gray-300)', label: 'Non testés' },
  ];

  // CSS conic-gradient donut
  let gradientParts = [];
  let cumulative = 0;
  donutData.forEach(d => {
    if (d.value > 0) {
      const pct = (d.value / total) * 100;
      gradientParts.push(`${d.color} ${cumulative}% ${cumulative + pct}%`);
      cumulative += pct;
    }
  });
  const gradient = total > 0
    ? `conic-gradient(${gradientParts.join(', ')})`
    : 'conic-gradient(var(--gray-200) 0% 100%)';

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h2><HiOutlineChartBar className="me-2" style={{ color: 'var(--info)' }} />Rapports</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{project.name}</p>
        </div>
      </div>
      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="report-kpi-card">
            <div className="report-kpi-icon" style={{ background: 'rgba(15,27,45,0.08)', color: 'var(--navy-600)' }}><FiTrendingUp /></div>
            <div className="report-kpi-value">{executedPct}%</div>
            <div className="report-kpi-label">Taux d'exécution</div>
            <div className="report-kpi-sub">{executed}/{total} exécutés</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report-kpi-card">
            <div className="report-kpi-icon" style={{ background: 'var(--green-100)', color: 'var(--green-600)' }}><FiCheckCircle /></div>
            <div className="report-kpi-value" style={{ color: 'var(--green-600)' }}>{successPct}%</div>
            <div className="report-kpi-label">Taux de réussite</div>
            <div className="report-kpi-sub">{stats.OK} réussis</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report-kpi-card">
            <div className="report-kpi-icon" style={{ background: 'var(--danger-light)', color: 'var(--danger)' }}><FiXCircle /></div>
            <div className="report-kpi-value" style={{ color: 'var(--danger)' }}>{failPct}%</div>
            <div className="report-kpi-label">Taux d'échec</div>
            <div className="report-kpi-sub">{stats.KO} échoués</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="report-kpi-card">
            <div className="report-kpi-icon" style={{ background: 'var(--info-light)', color: 'var(--info)' }}><FiTarget /></div>
            <div className="report-kpi-value">{coveragePct}%</div>
            <div className="report-kpi-label">Couverture</div>
            <div className="report-kpi-sub">{coveredReqs}/{totalReqs} exigences</div>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        {/* Donut Chart */}
        <div className="col-md-5">
          <div className="card h-100">
            <div className="card-header">Répartition des statuts</div>
            <div className="card-body d-flex align-items-center justify-content-center">
              <div className="donut-chart-wrapper">
                <div className="donut-chart" style={{ background: gradient }}>
                  <div className="donut-hole">
                    <div className="donut-total">{total}</div>
                    <div className="donut-label">Tests</div>
                  </div>
                </div>
                <div className="donut-legend">
                  {donutData.map(d => (
                    <div key={d.key} className="donut-legend-item">
                      <span className="donut-legend-color" style={{ background: d.color }}></span>
                      <span className="donut-legend-label">{d.label}</span>
                      <span className="donut-legend-value">{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bar Chart per Requirement */}
        <div className="col-md-7">
          <div className="card h-100">
            <div className="card-header">Progression par exigence</div>
            <div className="card-body reports-scroll">
              {reqStats.map(r => (
                <div key={r.id} className="bar-chart-row">
                  <div className="bar-chart-label">
                    <span className="req-id" style={{ fontSize: '0.6rem' }}>{r.exg_id}</span>
                  </div>
                  <div className="bar-chart-bar-wrapper">
                    <div className="bar-chart-bar">
                      {r.total > 0 && <>
                        <div className="bar-ok" style={{ width: `${(r.ok / r.total) * 100}%` }}></div>
                        <div className="bar-ko" style={{ width: `${(r.ko / r.total) * 100}%` }}></div>
                      </>}
                    </div>
                  </div>
                  <div className="bar-chart-value">{r.pct}%</div>
                </div>
              ))}
              {reqStats.length === 0 && <p className="text-muted text-center small">Aucune exigence</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="card">
        <div className="card-header"><FiActivity className="me-2" />Détail par exigence</div>
        <div className="table-responsive reports-scroll">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Exigence</th>
                <th>Description</th>
                <th className="text-center">Total</th>
                <th className="text-center" style={{ color: 'var(--green-600)' }}>OK</th>
                <th className="text-center" style={{ color: 'var(--danger)' }}>KO</th>
                <th className="text-center" style={{ color: 'var(--info)' }}>EC</th>
                <th className="text-center" style={{ color: 'var(--warning)' }}>BL</th>
                <th className="text-center" style={{ color: 'var(--gray-500)' }}>NT</th>
                <th>Réussite</th>
              </tr>
            </thead>
            <tbody>
              {project.requirements.map(req => {
                const tc = req.test_cases;
                const s = { NT: 0, EC: 0, OK: 0, KO: 0, BL: 0 };
                tc.forEach(t => { s[t.statut]++; });
                const pct = tc.length > 0 ? ((s.OK / tc.length) * 100).toFixed(0) : 0;
                return (
                  <tr key={req.id}>
                    <td><span className="req-id">{req.exg_id}</span></td>
                    <td className="small">{req.exigence}</td>
                    <td className="text-center fw-bold">{tc.length}</td>
                    <td className="text-center">{s.OK || '-'}</td>
                    <td className="text-center">{s.KO || '-'}</td>
                    <td className="text-center">{s.EC || '-'}</td>
                    <td className="text-center">{s.BL || '-'}</td>
                    <td className="text-center">{s.NT || '-'}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="trace-coverage-bar" style={{ width: '60px' }}>
                          <div className="trace-coverage-fill" style={{ width: `${pct}%` }}></div>
                        </div>
                        <span className="small fw-500">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
